import {
  type DocumentNode,
  type OperationVariables,
  type QueryHookOptions,
  type QueryResult,
  type TypedDocumentNode,
  useQuery,
} from '@apollo/client';

import { useFhirRendererClient } from '../core/RenderResource';

export type FhirQueryResult<
  TData,
  TVariables extends OperationVariables,
> = QueryResult<TData, TVariables> & {
  /** False while the app has no FHIR client (session initializing, feature off) — the query is skipped. */
  enabled: boolean;
};

/**
 * The paved path for FHIR GraphQL reads: Apollo useQuery bound to the client
 * the FhirRendererProvider carries, skipped automatically until that client
 * exists. Reach for useFhirRendererClient only for imperative work — event-
 * handler fetches, cache eviction, lazy/suspense hook variants.
 */
export function useFhirQuery<
  TData,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>,
): FhirQueryResult<TData, TVariables> {
  const client = useFhirRendererClient();
  const result = useQuery<TData, TVariables>(query, {
    fetchPolicy: 'cache-and-network',
    ...options,
    client,
    skip: !client || options?.skip,
  });
  return { ...result, enabled: Boolean(client) };
}
